// Canonical claim-status semantics — frontend mirror of
// backend/services/claimStatus.js.
//
// Mirrored the same way utils/productCatalog.js mirrors its backend twin: the
// backend owns the definition, this file restates it so a screen never has to
// invent one. Keep the two in step; a drift between them is exactly the bug
// this module was created to close.
//
// MyClaimsScreen carried its own list — ['new','open','review','investigation'] —
// which omitted evaluation, negotiation, approved, reported and reopened. Sofía
// has 4 live cases and the "Active Cases" tile said 1. Nothing was wrong with
// the data; the screen was counting 4 of the 9 non-terminal statuses and
// labelling the result "Active".
//
// See the backend module for why CLOSED and RESOLVED are separate sets.

/** Every status the Claim model accepts, in lifecycle order. */
export const ALL_STATUSES = Object.freeze([
  'reported', 'new', 'open', 'review', 'investigation', 'evaluation',
  'negotiation', 'approved', 'denied', 'closed', 'reopened',
]);

/** Still being worked — nothing terminal has happened. */
export const OPEN_STATUSES = Object.freeze([
  'reported', 'new', 'open', 'review', 'investigation', 'evaluation',
  'negotiation', 'approved', 'reopened',
]);

/** Newly arrived, not yet triaged. A strict subset of OPEN. */
export const PENDING_STATUSES = Object.freeze(['reported', 'new']);

/** Literally closed: a completed lifecycle with a settlement date. */
export const CLOSED_STATUSES = Object.freeze(['closed']);

/** Finished, settled or not. CLOSED ∪ {'denied'}. The complement of OPEN. */
export const RESOLVED_STATUSES = Object.freeze(['closed', 'denied']);

const OPEN_SET     = new Set(OPEN_STATUSES);
const CLOSED_SET   = new Set(CLOSED_STATUSES);
const RESOLVED_SET = new Set(RESOLVED_STATUSES);

const norm = (s) => String(s ?? '').trim().toLowerCase();

/** Is this claim still being worked? */
export const isOpenStatus = (s) => OPEN_SET.has(norm(s));

/** Is this claim literally closed (settled)? */
export const isClosedStatus = (s) => CLOSED_SET.has(norm(s));

/** Is this claim finished — closed or denied? */
export const isResolvedStatus = (s) => RESOLVED_SET.has(norm(s));

/** How many of `claims` are still being worked. Accepts any {status} shape. */
export const countOpen = (claims) =>
  (claims || []).filter(c => isOpenStatus(c?.status)).length;

const claimStatus = {
  ALL_STATUSES,
  OPEN_STATUSES,
  PENDING_STATUSES,
  CLOSED_STATUSES,
  RESOLVED_STATUSES,
  isOpenStatus,
  isClosedStatus,
  isResolvedStatus,
  countOpen,
};

export default claimStatus;
